import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  Paper,
  Skeleton,
  TextField,
  ThemeProvider,
  Toolbar,
  Typography,
  createTheme,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import {
  getVehicleById,
  getVehicles,
  getVehiclesCompany,
} from "../../../api/vehicleApi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { createClientLinkVehicle, getClientById } from "../../../api/clientApi";
import { VehicleData } from "../../../types/vehicleData";
import { formatPhone, maskDocument } from "../../../services/formatDocument";
import { LoadingButton } from "@mui/lab";

const defaultTheme = createTheme({
  typography: {
    fontFamily: "Poppins, sans-serif",
  },
});

const styles = {
  formControl: {
    fullWidth: true,
    marginTop: -2,
    "& .MuiSelect-root": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
      "&:focus": {
        backgroundColor: "#ffddec",
        borderColor: "brown",
      },
      "&:before": {
        borderColor: "orange",
      },
      "&:after": {
        borderColor: "green",
      },
    },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      borderRadius: 3,
      boxShadow: 6,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#303030",
      color: "#303030",
      borderWidth: "2px",
    },
  },
  button: {
    height: 50,
    marginTop: 6,
    backgroundColor: "#303030",
    borderRadius: 2,
    alignItems: "center",
    ":hover": {
      bgcolor: "#5c5c5c",
    },
  },
};

interface FieldMappings {
  [key: string]: string;
}
const fieldMappings: FieldMappings = {
  telefone_contato_cliente: "Telefone",
  documento_identificador_cliente: "Documento",
};

const AddVehiclePage: React.FC = () => {
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const token = localStorage.getItem("token");
  const [vehicles, setVehicles] = useState<VehicleData[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleData | null>(
    null
  );
  const [vehicleDetails, setVehicleDetails] = useState<VehicleData | null>(
    null
  );
  const [showSelect, setShowSelect] = useState(false);
  const [clientData, setClientData] = useState<any>(null);
  const [availableVehicles, setAvailableVehicles] = useState<VehicleData[]>([]);

  const fetchClients = useCallback(async (companyId?: string) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }
      const response = companyId
        ? await getVehiclesCompany(token, companyId)
        : await getVehicles(token);

      const vehiclesData = response.data || [];
      setVehicles(vehiclesData);
    } catch (error) {
      console.error("Erro ao buscar veículos:", error);
      setVehicles([]);
    }
  }, []);

  useEffect(() => {
    const fetchClientData = async () => {
      try {
        if (token && clientId !== undefined) {
          const data = await getClientById(token, clientId);
          setClientData(data);

          if (data.id_empresa) {
            await fetchClients(data.id_empresa);
          } else {
            await fetchClients();
          }
        } else {
          console.error("Token não encontrado ou clientId é indefinido");
        }
      } catch (error) {
        console.error("Erro ao buscar detalhes do cliente:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchClientData();
  }, [token, clientId, fetchClients]);

  useEffect(() => {
    if (Array.isArray(vehicles)) {
      const filteredVehicles = vehicles.filter(
        (vehicle) => vehicle.id_cliente === null
      );
      setAvailableVehicles(filteredVehicles);
    } else {
      setAvailableVehicles([]);
    }
  }, [vehicles]);

  const handleVehicleSelect = async (vehicle: VehicleData | null) => {
    try {
      setSelectedVehicle(vehicle);

      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("Token de autenticação não encontrado");
      }

      const vehicleDetailsData = await getVehicleById(
        token,
        String(vehicle?.id)
      );
      setVehicleDetails(vehicleDetailsData);
    } catch (error) {
      console.error("Erro ao buscar detalhes do veículo:", error);
    }
  };
  const handleVincularVeiculo = async () => {
    setLoadingSubmit(true);
    try {
      if (!token) {
        throw new Error("Token de autenticação não está disponível.");
      }

      if (!clientId || !selectedVehicle?.id) {
        throw new Error("ID do veículo ou do cliente não disponível.");
      }

      await createClientLinkVehicle(selectedVehicle, token, clientId);
      toast.success("Veículo vinculado ao cliente com sucesso!");
      navigate("/cliente");
    } catch (error) {
      toast.error("Erro ao vincular veículo ao cliente.");
      console.error("Erro ao vincular veículo ao cliente:", error);
    } finally {
      setLoadingSubmit(false);
    }
  };

  const handleVincularNovoVeiculo = () => {
    if (clientData && clientData.id_empresa) {
      navigate(`/novoVeiculo/${clientId}?idEmpresa=${clientData.id_empresa}`);
    } else {
      navigate(`/novoVeiculo/${clientId}`);
    }
  };

  const handleVincularVeiculoExistente = () => {
    setShowSelect(true);
  };

  return (
    <ThemeProvider theme={defaultTheme}>
      <Box sx={{ display: "flex" }}>
        <Box
          sx={{
            flexGrow: 1,
            overflow: "auto",
          }}
        >
          <Toolbar sx={{ mb: 2, mt: 2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  cursor: "pointer",
                  color: "red",
                },
              }}
              onClick={() => navigate("/cliente")}
            >
              <IconButton
                edge="start"
                color="inherit"
                aria-label="voltar"
                sx={{ color: "inherit" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h6"
                component="div"
                sx={{ color: "inherit", mt: 0.2 }}
              >
                Voltar
              </Typography>
            </Box>
          </Toolbar>

          <Container sx={{ mb: 4 }} maxWidth={"lg"}>
            <Box component="form" noValidate>
              <Typography
                variant="h5"
                fontStyle={"inherit"}
                sx={{ fontWeight: 400 }}
              >
                Adicionar Veículo ao Cliente
                <Divider
                  variant="middle"
                  sx={{
                    width: 120,
                    backgroundColor: "red",
                    height: 2,
                    marginLeft: 0,
                  }}
                />
              </Typography>
              <Grid sx={{ mt: 3 }} p={2} mr={4}>
                <Grid item xs={11} sm={10} md={8} lg={6}>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={150}
                      sx={{ borderRadius: 5 }}
                    />
                  ) : (
                    <Paper
                      elevation={4}
                      sx={{ p: 2, mb: 2, borderRadius: 5, width: "100%" }}
                    >
                      {clientData && (
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <Typography variant="h5" mr={2}>
                              Nome do Cliente:
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{ fontWeight: "bold" }}
                            >
                              {clientData.nome_cliente}
                            </Typography>
                          </Box>
                          <Divider sx={{ my: 2 }} />
                          <Grid container spacing={2}>
                            {Object.entries(clientData).map(([key, value]) => {
                              const mappedFieldName = fieldMappings[key] || key;

                              const excludedFields = [
                                "id",
                                "veiculos",
                                "nome_cliente",
                                "id_empresa",
                                "veiculos_relacao",
                                "codigo_dms_cliente",
                              ];

                              if (!excludedFields.includes(key)) {
                                let formattedValue: string;
                                if (typeof value === "string") {
                                  if (
                                    key === "documento_identificador_cliente"
                                  ) {
                                    formattedValue = maskDocument(value);
                                  } else if (
                                    key === "telefone_contato_cliente"
                                  ) {
                                    formattedValue = formatPhone(value);
                                  } else {
                                    formattedValue = value;
                                  }
                                } else {
                                  formattedValue = String(value);
                                }

                                return (
                                  <Grid item xs={12} sm={6} md={6} key={key}>
                                    <Typography sx={{ fontWeight: "bold" }}>
                                      {mappedFieldName}:
                                    </Typography>
                                    <Typography variant="body1">
                                      {formattedValue}
                                    </Typography>
                                  </Grid>
                                );
                              }
                              return null;
                            })}
                          </Grid>
                        </Box>
                      )}
                    </Paper>
                  )}
                </Grid>
              </Grid>
              <Grid container spacing={2} p={2} mt={-6}>
                <Grid item md={6} sm={6} xs={12}>
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={50}
                      sx={{ borderRadius: 5, mt: 5 }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      sx={styles.button}
                      fullWidth
                      onClick={handleVincularNovoVeiculo}
                    >
                      Vincular novo veículo
                    </Button>
                  )}
                </Grid>
                <Grid
                  item
                  md={6}
                  sm={6}
                  xs={12}
                  sx={{ marginTop: { xs: -6, sm: 0 } }}
                >
                  {loading ? (
                    <Skeleton
                      variant="rectangular"
                      height={50}
                      sx={{ borderRadius: 5, mt: 5 }}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      sx={styles.button}
                      fullWidth
                      onClick={handleVincularVeiculoExistente}
                    >
                      Vincular veículo existente
                    </Button>
                  )}
                </Grid>
              </Grid>
              {showSelect && (
                <Grid container spacing={3} mt={2} p={2}>
                  <Grid item xs={12}>
                    <div>
                      <Autocomplete
                        id="client-select"
                        options={availableVehicles}
                        getOptionLabel={(option) =>
                          `Chassi: ${option.chassi_veiculo} - Modelo: ${option.descricao_modelo} - Ano: ${option.descricao_ano_modelo}`
                        }
                        onChange={(event, value) => handleVehicleSelect(value)}
                        isOptionEqualToValue={(option, value) =>
                          option.id === value.id
                        }
                        noOptionsText="Nenhum veículo encontrado"
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Selecione o veículo"
                            variant="outlined"
                          />
                        )}
                      />

                      {vehicleDetails && (
                        <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 3 }}>
                          <Typography variant="h6" gutterBottom>
                            Detalhes do Veículo
                          </Typography>
                          <Box>
                            <Typography variant="body1" gutterBottom>
                              V.I.N: {vehicleDetails.chassi_veiculo}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Ano Veículo: {vehicleDetails.descricao_ano_modelo}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Modelo:
                              {vehicleDetails.descricao_modelo}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Cor:
                              {vehicleDetails.descricao_cor}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Km Inicial:
                              {vehicleDetails.km_veiculo}
                            </Typography>
                            <Typography variant="body1" gutterBottom>
                              Local de Armazenagem: {""}
                              {vehicleDetails.descricao_local}
                            </Typography>
                          </Box>
                        </Paper>
                      )}
                    </div>
                  </Grid>
                  <Grid item md={12} sm={12} xs={12}>
                    <LoadingButton
                      variant="contained"
                      fullWidth
                      loading={loadingSubmit}
                      sx={styles.button}
                      onClick={handleVincularVeiculo}
                    >
                      <Typography>Vincular Veículo</Typography>
                    </LoadingButton>
                  </Grid>
                </Grid>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AddVehiclePage;
